import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { FaLinkedinIn, FaFacebookF } from "react-icons/fa";
import { RiTwitterXFill } from "react-icons/ri";

interface WPPost {
  id: number;
  title: { rendered: string };
  content: { rendered: string };
  date: string;
  modified: string;
  author: number;
  slug?: string;
  _embedded?: {
    author?: { id: number; name: string; slug: string; acf?: { custom_avatar?: number | string; full_name?: string }; avatar_urls?: { [size: string]: string } }[];
    'wp:featuredmedia'?: { source_url: string }[];
    'wp:term'?: { id: number; name: string; slug: string; taxonomy: string }[][];
  };
  categories: number[];
  tags: number[];
}

interface TOCItem {
  id: string;
  text: string;
}

const RawHTML: React.FC<{ html: string }> = ({ html }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Append styles
    Array.from(tempDiv.querySelectorAll('style')).forEach(style => {
      document.head.appendChild(style);
    });

    // Append scripts
    Array.from(tempDiv.querySelectorAll('script')).forEach(oldScript => {
      const newScript = document.createElement('script');
      if (oldScript.src) {
        newScript.src = oldScript.src;
        newScript.async = false;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      document.body.appendChild(newScript);
      oldScript.remove();
    });

    containerRef.current.innerHTML = tempDiv.innerHTML;
  }, [html]);

  return <div ref={containerRef} className="blog-post-content" />;
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [post, setPost] = useState<WPPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorImages, setAuthorImages] = useState<Record<number, string>>({});
  const [tags, setTags] = useState<{ name: string; slug: string }[]>([]);
  const [toc, setToc] = useState<TOCItem[]>([]);
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);
  const [recentPosts, setRecentPosts] = useState<WPPost[]>([]);

  useEffect(() => {
    if (!slug) return;

    const fetchPost = async () => {
      try {
        const res = await axios.get<WPPost[]>(`https://blog.thecryptotutor.com/wp-json/wp/v2/posts?slug=${slug}&_embed`);
        const fetchedPost = res.data[0];
        if (!fetchedPost) throw new Error('Post not found');
        setPost(fetchedPost);

        // Author images
        const authorMap: Record<number, string> = {};
        const mainAuthor = fetchedPost._embedded?.author?.[0];
        if (mainAuthor) {
          if (mainAuthor.acf?.custom_avatar) {
            try {
              const avatarRes = await axios.get(`https://blog.thecryptotutor.com/wp-json/wp/v2/media/${mainAuthor.acf.custom_avatar}`);
              authorMap[mainAuthor.id] = avatarRes.data.source_url || '/default-avatar.jpg';
            } catch {
              authorMap[mainAuthor.id] = '/default-avatar.jpg';
            }
          } else if (mainAuthor.avatar_urls?.['96']) {
            authorMap[mainAuthor.id] = mainAuthor.avatar_urls['96'];
          } else {
            authorMap[mainAuthor.id] = '/default-avatar.jpg';
          }
        }
        setAuthorImages(authorMap);

        // Tags
        const postTags: { name: string; slug: string }[] = [];
        const termGroups = fetchedPost._embedded?.['wp:term'];
        termGroups?.forEach(group =>
          group.forEach(term => {
            if (term.taxonomy === 'post_tag') postTags.push({ name: term.name, slug: term.slug });
          })
        );
        setTags(postTags);

        // Featured image
        const img = fetchedPost._embedded?.['wp:featuredmedia']?.[0]?.source_url;
        setFeaturedImage(img || null);

        // Table of Contents
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(fetchedPost.content.rendered, 'text/html');
        const headings = Array.from(htmlDoc.querySelectorAll('h2, h3'));
        const tocItems: TOCItem[] = headings.map(h => {
          let id = h.getAttribute('id');
          if (!id) {
            id = h.textContent?.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || '';
            h.setAttribute('id', id);
          }
          return { id, text: h.textContent || '' };
        });
        setToc(tocItems);
        fetchedPost.content.rendered = htmlDoc.body.innerHTML;

        // Recent posts
        const recentRes = await axios.get<WPPost[]>(`https://blog.thecryptotutor.com/wp-json/wp/v2/posts?per_page=4&_embed`);
        setRecentPosts(recentRes.data.filter(p => p.slug !== slug));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) return <div className="text-center py-16">Loading...</div>;
  if (!post) return <div className="text-center py-16">Post not found</div>;

  const author = post._embedded?.author?.[0];
  const formattedDate = new Date(post.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  const currentURL = window.location.href;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Inject WP Block Styles directly for this template */}
      <style>{`
        /* === WordPress Block Headings === */
        .blog-post-content h1 { font-size: 40px !important; margin: 32px 0 16px !important; font-weight:700 !important; line-height:1.3 !important; color:#222 !important; }
        .blog-post-content h2 { font-size: 30px !important; margin:32px 0 16px !important; font-weight:700 !important; line-height:1.3 !important; color:#222 !important; }
        .blog-post-content h3 { font-size: 26px !important; margin:24px 0 12px !important; font-weight:700 !important; line-height:1.3 !important; color:#222 !important; }
        .blog-post-content h4 { font-size: 22px !important; margin:24px 0 12px !important; font-weight:700 !important; line-height:1.3 !important; color:#222 !important; }
        .blog-post-content h5 { font-size: 20px !important; margin:16px 0 8px !important; font-weight:700 !important; line-height:1.3 !important; color:#222 !important; }
        .blog-post-content h6 { font-size: 16px !important; margin:16px 0 8px !important; font-weight:700 !important; line-height:1.3 !important; color:#222 !important; }

        .blog-post-content p { margin:0 0 24px !important; line-height:1.7 !important; font-size:16px !important; color:#333 !important; }
        .blog-post-content ul, .blog-post-content ol { margin:0 0 24px 32px !important; }
        .blog-post-content li { margin: 8px 0 !important; }
        .blog-post-content img { max-width:100% !important; height:auto !important; border-radius:4px !important; }
        .blog-post-content blockquote { margin:24px 0 !important; padding:16px 24px !important; border-left:4px solid #ccc !important; font-style:italic !important; background:#f9f9f9 !important; }
        .blog-post-content pre { background:#f5f5f5 !important; padding:16px !important; overflow-x:auto !important; border-radius:4px !important; font-family:monospace !important; font-size:14px !important; }
        .blog-post-content table { width:100% !important; border-collapse:collapse !important;}
        .blog-post-content th, .blog-post-content td { border:1px solid #ddd !important; padding:12px !important; text-align:left !important; }
        .blog-post-content th { background:#f5f5f5 !important; font-weight:600 !important; }
        .blog-post-content a.wp-block-button__link { display:inline-block !important; padding:12px 24px !important; background-color:#0073aa !important; color:#fff !important; text-decoration:none !important; border-radius:4px !important; transition: background-color 0.3s !important; }
        .blog-post-content a.wp-block-button__link:hover { background-color:#005177 !important; }
      `}</style>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Article */}
          <article className="flex-1 bg-white rounded-lg shadow-sm p-6 lg:p-8">
            {featuredImage && <img src={featuredImage} alt={post.title.rendered} className="w-full object-cover rounded-lg mb-4 lg:hidden" />}
            <h1 className="text-3xl font-bold mb-2" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />

            {author && (
              <div className="flex items-center gap-4 mb-6 lg:flex-row">
                <img src={authorImages[author.id] || '/default-avatar.jpg'} alt={author.name} className="w-10 h-10 rounded-lg object-cover" />
                <div>
                  <p className="text-sm font-medium">
                    By: <Link to={`/author/${author.slug}`} className="hover:text-blue-600">{author.acf?.full_name || author.name}</Link>
                  </p>
                  <p className="text-sm text-gray-600">{formattedDate}</p>
                </div>
              </div>
            )}

            {/* WP HTML */}
            <RawHTML html={post.content.rendered} />

            {/* Mobile/Table TOC */}
            {toc.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6 lg:hidden">
                <h2 className="text-lg font-semibold mb-4">Table of Contents</h2>
                <ul className="space-y-3">
                  {toc.map(item => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="block text-gray-700 hover:text-gray-900">{item.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>

          {/* Desktop Sidebar ONLY */}
          <aside className="hidden lg:block w-80 flex-shrink-0 space-y-6 sticky top-20">
            {featuredImage && <img src={featuredImage} alt={post.title.rendered} className="w-full object-cover rounded-lg" />}
            {toc.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h2 className="text-lg font-semibold mb-4">Table of Contents</h2>
                <ul className="space-y-3">
                  {toc.map(item => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="block relative text-gray-700 hover:text-gray-900">{item.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>

        {/* Related Posts */}
        {recentPosts.length > 0 && (
          <section className="mt-12">
            <h2 className="text-3xl font-bold mb-6">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentPosts.map(rp => (
                <Link key={rp.id} to={`/blog/${rp.slug}`} className="group h-full">
                  <Card hover className="flex flex-col h-full overflow-hidden">
                    <div className="aspect-video overflow-hidden flex-shrink-0">
                      <img
                        src={rp._embedded?.['wp:featuredmedia']?.[0]?.source_url || '/default-image.jpg'}
                        alt={rp.title.rendered}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3
                        className="font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors"
                        dangerouslySetInnerHTML={{ __html: rp.title.rendered }}
                      />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default BlogPost;
